/* Signing in and creating an account.
 *
 * There is no invite field: the private beta is over and the gate was removed
 * rather than switched off.
 *
 * The bug this screen is written to make impossible: the old version wrapped
 * both the credential request *and* the app's first load in one try/catch, so
 * when the app failed to load after a correct password you were told "could
 * not reach the server" — while signed in, with a stored token, looking at a
 * form you had already completed successfully.
 *
 * Here the two are structurally separate. This component's only job is to
 * exchange credentials for a session. What happens next is the router's
 * problem, and a failure there cannot be reported as a failure here.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { AuthSuccess, SignupPolicy, User } from '@shared/types';
import { api, errorMessage, readLocal, writeLocal } from '../api';
import { useSession } from '../session';
import Icon from '../ui/Icon';
import type { IconName } from '../ui/Icon';

/* What the product does, said in three lines beside the form. Somebody
   arriving from an invitation should know what they are signing up to
   before they are asked for a password, not after. */
const PROMISES: { icon: IconName; title: string; text: string }[] = [
  { icon: 'sparkle', title: 'A day that plans itself', text: 'Tell Amon what you owe. It finds the hours around what is already fixed.' },
  { icon: 'hobbies', title: 'Room for the rest of your life', text: 'The gym, friends, the guitar — held back before work gets the week.' },
  { icon: 'bell', title: 'Reminders that reach you', text: 'Ten minutes before, on the hour, and again if you have not started.' }
];

type Tab = 'signin' | 'signup';

interface TotpChallenge { challenge: string; email: string; password: string }

export default function AuthScreen() {
  const { signIn } = useSession();
  /* Read through the guarded accessor: a browser that refuses storage throws
     here, and a throw inside a useState initialiser is a blank sign-in page. */
  const [tab, setTab] = useState<Tab>(() =>
    readLocal('amon_returning') ? 'signin' : 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [totp, setTotp] = useState<TotpChallenge | null>(null);
  const [policy, setPolicy] = useState<SignupPolicy | null>(null);

  /* Whether a new account has to confirm its address changes what to say after
     the button, so it is asked up front — but never blocks the form. */
  useEffect(() => {
    let live = true;
    void api<SignupPolicy>('GET', '/api/signup-policy')
      .then(r => { if (live && r.ok) setPolicy(r); });
    return () => { live = false; };
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    const endpoint = tab === 'signup' ? 'signup' : 'login';
    const r = await api<AuthSuccess>('POST', `/api/auth/${endpoint}`, {
      email: email.trim(),
      password,
      ...(tab === 'signup'
        ? { tz: Intl.DateTimeFormat().resolvedOptions().timeZone }
        : {})
    });
    setBusy(false);

    /* The password was right and a second factor is enrolled. Not an error to
       show in red — it is the next step, so the form becomes a code field
       rather than the screen becoming a failure. */
    if (!r.ok && r.error === 'totp_required') {
      const challenge = (r as unknown as { challenge?: string }).challenge;
      if (challenge) { setTotp({ challenge, email: email.trim(), password }); return; }
    }

    if (!r.ok) {
      setError(errorMessage(r, tab === 'signup'
        ? 'Could not create the account.'
        : 'Could not sign you in.'));
      return;
    }

    writeLocal('amon_returning', '1');             // next visit opens on Sign in
    signIn(r.user, r.token);
  }

  if (totp) {
    return <SecondFactor
      challenge={totp.challenge}
      onDone={(user, token) => signIn(user, token)}
      onRestart={() => { setTotp(null); setPassword(''); }} />;
  }

  return (
    <div className="auth-wrap">
      <div className="auth-split">
      <aside className="auth-hero" aria-label="What Amon does">
        <div className="auth-brand">
          <div className="brand-mark">A</div>
          <div><strong>Amon</strong><span>your planning agent</span></div>
        </div>
        <h1 className="auth-headline">Plan less.<br />Do more of what matters.</h1>
        <ul className="auth-promises">
          {PROMISES.map(p => (
            <li key={p.title}>
              <span className="section-icon" aria-hidden="true"><Icon name={p.icon} size={18} /></span>
              <div><strong>{p.title}</strong><span>{p.text}</span></div>
            </li>
          ))}
        </ul>
      </aside>

      <div className="auth-card">
        <div className="auth-brand auth-brand-small">
          <div className="brand-mark">A</div>
          <div><strong>Amon</strong><span>your planning agent</span></div>
        </div>

        <div className="segmented auth-tabs" role="tablist">
          {(['signin', 'signup'] as const).map(t => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'is-active' : ''}
              onClick={() => { setTab(t); setError(null); }}>
              {t === 'signin' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>

        <form onSubmit={submit}>
          <label className="auth-field">
            <span>Email</span>
            <span className="input-icon"><Icon name="send" size={16} /></span>
            <input
              name="email" type="email" required autoComplete="email"
              placeholder="you@work.com"
              value={email} onChange={e => setEmail(e.target.value)} />
          </label>

          <label className="auth-field">
            <span>Password</span>
            <span className="input-icon"><Icon name="lock" size={16} /></span>
            <input
              name="password" type="password" required minLength={8}
              autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
              placeholder="At least 8 characters"
              value={password} onChange={e => setPassword(e.target.value)} />
          </label>

          {error && <p className="auth-err" role="alert">{error}</p>}

          {tab === 'signup' && policy?.requireVerified && (
            <p className="auth-hint">
              You will need to confirm your email address before the app opens up.
              {policy.mail === 'console' && ' No mail provider is configured, so the code goes to the server log.'}
            </p>
          )}

          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy
              ? (tab === 'signup' ? 'Creating…' : 'Signing in…')
              : (tab === 'signup' ? 'Create account' : 'Sign in')}
          </button>
        </form>

        <p className="auth-foot">
          <Icon name="smartphone" size={14} /> Your plan syncs across every device you sign in on.
        </p>
      </div>
      </div>
    </div>
  );
}

/* ── the second half of a two-factor sign-in ─────────────────────
   A replacement for the password form rather than a dialog over it: the
   password is already accepted, and leaving it on screen behind a modal
   invites people to retype it, which is how a correct password ends up in the
   wrong box. */
function SecondFactor({ challenge, onDone, onRestart }: {
  challenge: string;
  onDone: (user: User, token: string) => void;
  onRestart: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    /* The server's route is /auth/login/2fa — the second half of a sign-in,
       named after it. This once called a path that did not exist, so every
       account with two-factor on could not sign in to this client; the smoke
       suite found it the day it started reading this client's source. */
    const r = await api<AuthSuccess>('POST', '/api/auth/login/2fa', { challenge, code: code.trim() });
    setBusy(false);
    if (!r.ok) { setError(errorMessage(r, 'That code was not accepted.')); return; }
    writeLocal('amon_returning', '1');
    onDone(r.user, r.token);
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="section-icon auth-lock" aria-hidden="true"><Icon name="lock" size={22} /></div>
        <div className="auth-brand auth-brand-center">
          <div><strong>One more step</strong><span>two-factor authentication</span></div>
        </div>
        <form onSubmit={submit}>
          <label className="auth-field">
            <span>Six-digit code</span>
            <input
              inputMode="numeric" autoComplete="one-time-code" autoFocus
              placeholder="000000" maxLength={20}
              value={code} onChange={e => setCode(e.target.value)} />
          </label>
          {error && <p className="auth-err" role="alert">{error}</p>}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
        </form>
        <p className="auth-foot">
          Lost the device? A recovery code works here too.{' '}
          <button className="linkish" type="button" onClick={onRestart}>Start again</button>
        </p>
      </div>
    </div>
  );
}
